import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { IUser } from '@boma/client-shared';
import { DbAction } from '@boma/shared/entities';
import { IUserNotificationTrigger } from '@boma/supplier-portal/shared/entities';
import { getSelectedOrganizationInfo } from '@boma/supplier-portal/shared/state';
import { BreadcrumbService } from '@boma/ui';
import { Store } from '@ngrx/store';
import { MessageService } from 'primeng/api';
import { combineLatest, Observable } from 'rxjs';
import { filter, finalize, map, take } from 'rxjs/operators';
import { NotificationsApiService } from '../../../../supplier-portal/shared/services/src/lib/notifications/notifications-api.service';
import { UserNotificationTriggersService } from './user-notification-triggers.service';

interface AddOrRemoveTriggerUser {
  notificationType: number;
  userId: string;
  existingTriggerId?: string;
  locationId?: string;
  targetId?: string;
  action: DbAction;
}

@Component({
  selector: 'boma-user-notifications',
  templateUrl: './user-notifications.component.html',
  styleUrls: ['./user-notifications.component.scss'],
})
export class UserNotificationsComponent implements OnInit, OnDestroy {
  @Input() showFilterBlock: boolean;
  @Input() locationId?: string;
  @Input() targetId?: string;

  @Input() clearOnDestroy = true;

  cols$: Observable<
    { field: any; header: string; type: string; value?: any }[]
  >;

  form: {
    updates: Map<string, Map<number, AddOrRemoveTriggerUser>>;
  } = {
    updates: new Map<string, Map<number, AddOrRemoveTriggerUser>>(),
  };

  userNotificationTriggers$: Observable<
    {
      user: IUser;
      notificationTypeTriggers: { [typeId: number]: IUserNotificationTrigger };
    }[]
  >;

  loading = false;

  private _filters$ = this.notificationTriggersService.userNotificationFilters$;
  pageSize$ = this._filters$.pipe(map((filters) => filters?.perPage));

  isFirstPage$ =
    this.notificationTriggersService.userNotificationTriggers$.pipe(
      map((t) => !t?.previousPage)
    );
  isLastPage$ = this.notificationTriggersService.userNotificationTriggers$.pipe(
    map((t) => !t?.nextPage)
  );
  page$ = this._filters$.pipe(map((filters) => filters?.page));

  totalItems$ = this.notificationTriggersService.userNotificationTriggers$.pipe(
    map((t) => t?.totalItems)
  );

  constructor(
    private notificationsApiService: NotificationsApiService,
    private notificationTriggersService: UserNotificationTriggersService,
    private store: Store,
    private breadcrumbService: BreadcrumbService,
    private messageService: MessageService
  ) {}

  ngOnDestroy(): void {
    if (this.clearOnDestroy) {
      this.notificationTriggersService.clear();
    }

    this.breadcrumbService.setItems([]);
  }

  ngOnInit(): void {
    this.breadcrumbService.setItems(
      [
        {
          label: 'Notifications',
        },
        {
          label: 'User Notification Triggers',
        },
      ],
      [
        {
          label: 'Save Notifications',
          action: () => {
            this.save();
          },
        },
      ]
    );

    this.cols$ = this.notificationsApiService.notificationTypes$.pipe(
      map((types) => [
        { field: 'user', header: 'User', type: 'user' },
        ...types.map((t) => ({
          field: 'notificationTypeTriggers',
          header: t.name,
          type: 'nt',
          value: t.id,
        })),
      ])
    );

    this.userNotificationTriggers$ = combineLatest([
      this.notificationsApiService.notificationTypes$,
      this.notificationTriggersService.userNotificationTriggers$,
    ]).pipe(
      map(([notificationTypes, response]) => {
        if (!response?.items) return [];
        return response.items.map((userTriggers) => {
          const triggers: { [typeId: number]: IUserNotificationTrigger } = {};
          for (const type of notificationTypes) {
            triggers[type.id] = userTriggers.notificationTriggers.filter(
              (trigger) => {
                return (
                  trigger.notificationType.id === type.id &&
                  ((!trigger.location && !trigger.targetId) ||
                    (!!this.locationId &&
                      trigger.location?.id === this.locationId) ||
                    (!!this.targetId && trigger.targetId === this.targetId))
                );
              }
            )[0];
          }
          this.form.updates.set(
            userTriggers.user.id,
            new Map<number, AddOrRemoveTriggerUser>()
          );
          return {
            user: userTriggers.user,
            notificationTypeTriggers: triggers,
          };
        });
      })
    );

    combineLatest([
      this.store.select(getSelectedOrganizationInfo),
      this.notificationTriggersService.userNotificationFilters$,
    ])
      .pipe(
        filter(([org, _]) => !!org),
        take(1)
      )
      .subscribe(([org, filters]) => {
        if (!filters)
          this.notificationTriggersService.setFilters({
            entityId: org.id,
            notificationTypes: [],
            locationIds: this.locationId ? [this.locationId] : [],
            targetIds: this.targetId ? [this.targetId] : [],
            page: 1,
            perPage: 25,
          });

        this.loadNotifications();
      });
  }

  loadNotifications() {
    this._commonLoader(
      this.notificationTriggersService.loadUserNotifications()
    ).subscribe();
  }

  nextPage() {
    this._commonLoader(this.notificationTriggersService.nextPage()).subscribe();
  }

  previousPage() {
    this._commonLoader(
      this.notificationTriggersService.previousPage()
    ).subscribe();
  }

  _commonLoader(body: Observable<any>) {
    this.loading = true;
    return body.pipe(finalize(() => (this.loading = false)));
  }

  save() {
    const updates = Array.from(this.form.updates.keys()).some(
      (key) => !!Array.from(this.form.updates.get(key).keys()).length
    );
    if (!updates) {
      this.messageService.add({
        severity: 'info',
        detail: 'No changes were made',
        summary: 'Update cancelled',
      });
      return;
    }
    return this.store
      .select(getSelectedOrganizationInfo)
      .pipe(take(1))
      .subscribe((org) => {
        const params = Array.from(this.form.updates.keys())
          .map((userId) => {
            return Array.from(this.form.updates.get(userId).keys()).map(
              (typeId) => {
                const obj = this.form.updates.get(userId).get(Number(typeId));
                return {
                  userId: obj.userId,
                  id: obj.existingTriggerId,
                  notificationTypeId: obj.notificationType,
                  locationId: this.locationId,
                  targetId: this.targetId,
                  action: obj.action,
                };
              }
            );
          })
          .reduce(
            (previousValue, currentValue) => [
              ...previousValue,
              ...currentValue,
            ],
            []
          );
        this.notificationsApiService
          .addOrRemoveNotificationTriggerUsers(org.id, params)
          .subscribe();
      });
  }

  onChange(
    event: any,
    userId: string,
    typeId: number,
    existingTriggerId?: string
  ) {
    const checked: boolean = event.target.checked;

    if (existingTriggerId && checked) {
      // Entry already exists, no need to mark for adding
      this.form.updates.get(userId).delete(typeId);
    } else if (!existingTriggerId && !checked) {
      // Cannot mark for removal on an entry that does not exist
      this.form.updates.get(userId).delete(typeId);
    } else {
      this.form.updates.get(userId).set(typeId, {
        notificationType: typeId,
        existingTriggerId,
        userId,
        action: checked ? DbAction.Add : DbAction.Remove,
      });
    }
  }
}
